import { useEffect, useRef } from 'react';

const THRESHOLD = 64;
const MAX_PULL = 96;

/**
 * Dokunmatik ekranda sayfa başında aşağı çekince akış yerinde yenilenir (3 Kasım: dokunmatikte
 * "Gelişmeleri yenile" düğmesi gizli; iOS uygulaması WebView olduğu için "standalone" değil ve
 * önceden orada hiç yenileme yolu kalmıyordu). Yalnız akış ekranın üst yarısındayken devreye girer;
 * o sırada tarayıcının sayfayı baştan yükleyen kendi çekmesi `overscroll-behavior-y: contain` ile
 * kapatılır. Akış aşağıdaysa (ör. lig sayfasının başı) tarayıcınınki çalışır.
 * Çekme göstergesi her harekette yeniden çizim yapmamak için doğrudan DOM üzerinden güncellenir.
 */
export default function usePullToRefresh(
  indicator: React.RefObject<HTMLElement>,
  onRefresh: () => void,
  enabled: boolean,
) {
  const refresh = useRef(onRefresh);
  refresh.current = onRefresh;

  useEffect(() => {
    const touch = window.matchMedia?.('(pointer: coarse)').matches || 'ontouchstart' in window;
    if (!enabled || !touch) return undefined;
    const html = document.documentElement;
    const previousOverscroll = html.style.overscrollBehaviorY;
    // Akış (göstergenin kapsayıcısı) ekranın üst yarısında ve sayfa en başta mı.
    const feedNearTop = () => {
      const root = indicator.current?.parentElement;
      return !!root && window.scrollY <= 0 && root.getBoundingClientRect().top < window.innerHeight / 2;
    };
    const syncOverscroll = () => {
      html.style.overscrollBehaviorY = feedNearTop() ? 'contain' : previousOverscroll;
    };
    let start: number | null = null;
    let distance = 0;
    const paint = () => {
      const node = indicator.current;
      if (!node) return;
      node.style.transform = `translateY(${distance}px)`;
      node.style.opacity = String(Math.min(1, distance / THRESHOLD));
      node.classList.toggle('is-ready', distance >= THRESHOLD);
    };
    const onStart = (event: TouchEvent) => {
      start = feedNearTop() && event.touches.length === 1 ? event.touches[0].clientY : null;
      distance = 0;
    };
    const onMove = (event: TouchEvent) => {
      if (start === null) return;
      if (window.scrollY > 0) {
        start = null;
        distance = 0;
      } else {
        distance = Math.max(0, Math.min(MAX_PULL, (event.touches[0].clientY - start) / 2));
      }
      paint();
    };
    const onEnd = () => {
      if (start !== null && distance >= THRESHOLD) refresh.current();
      start = null;
      distance = 0;
      paint();
    };
    syncOverscroll();
    window.addEventListener('scroll', syncOverscroll, { passive: true });
    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onEnd);
    window.addEventListener('touchcancel', onEnd);
    return () => {
      html.style.overscrollBehaviorY = previousOverscroll;
      window.removeEventListener('scroll', syncOverscroll);
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);
    };
  }, [enabled, indicator]);
}
